import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import React from 'react';
import { withRouter } from 'react-router-dom';
import { createLoadingSelector } from '@hobs/utils/loading';
import { ExcelExportColumn } from '@progress/kendo-react-excel-export';
import ShortId from 'shortid';
import WithAnnexFilter from '@hobs/hocs/WithAnnexFilter';
import WithQuickSearch from '@hobs/hocs/WithQuickSearch';
import WithVisibleColumns from '@hobs/hocs/WithVisibleColumns';
import WithRoles from '@hobs/hocs/WithRoles';
import Tooltip from '@hobs/components/Tooltip';
import Money from '@hobs/components/Money';
import Tabs from '@hobs/components/Tabs';
import { RaisedButton } from '@hobs/components/Buttons';
import Switch from '@hobs/components/Switch';
import Toast from '@hobs/components/Toast';
import Dialog from '@hobs/components/Dialog';
import Textarea from '@hobs/components/Textarea';
import Dropdown from '@hobs/components/Dropdown';
import { Formik, Form, Field } from 'formik';
import { queryOData, resetOData, queryXLSOData } from '@hobs/actions/odata';
import { getAnnexes } from '@hobs/actions/annex';
import { getAnnexOverviewGrid } from '@hobs/actions/gridSelections';
import {
  bulkAssignForSubmit,
  bulkAssign,
  bulkAppoint,
  canBulkAssign,
  getAnnexFilteredServiceRequestIds
} from '@hobs/actions/assignmentServiceRequest';
import _AnnexOverview from '@hobs/containers/AnnexOverview';
import Discussion from '@hobs/containers/Discussion';
import InvoiceDialog from '@hobs/containers/InvoiceDialog';
import InvoiceCreateDialog from '@hobs/containers/InvoiceCreateDialog';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import { ServiceRequestNavContext } from '@hobs/components/ServiceRequestNavContext';

/**
 * Keeping this to be able to extend it in near future.
 */
class AnnexOverview extends _AnnexOverview {
  getDiscussionPath(srId) {
    const { match } = this.props;
    return `${match.url}/discussion/${srId}`;
  }

  getDefaultColumns() {
    const { t } = this.props;
    const data = this._getDefaultColumns();

    if (data.AdditionalInfo) {
      delete data.AdditionalInfo;
    }

    if (data.RateFrom) {
      delete data.RateFrom;
    }
    if (data.RateTo) {
      delete data.RateTo;
    }

    if (data.IsPaid) {
      delete data.IsPaid;
    }

    if (data.EstimatedTotalCost) {
      delete data.EstimatedTotalCost;
    }

    if (data.EstimatedGrossAmount) {
      delete data.EstimatedGrossAmount;
    }

    if (data.ActivityName) {
      delete data.ActivityName;
    }

    if (data.Suppliers) {
      delete data.Suppliers;
    }

    if (data.IsSupplierDeliveryFinalized) {
      delete data.IsSupplierDeliveryFinalized;
    }

    data.InvoiceNumber = {
      title: t('Invoice ref.'),
      columnMenu: true,
      default: false,
      visible: false,
      cell: ({ dataItem }) => {
        return (
          <td>
            {dataItem.InvoiceReferenceId && (
              <>
                <Link
                  to={`/annex-overview/invoice/${dataItem.InvoiceReferenceId}`}
                >
                  {dataItem.InvoiceNumber}
                </Link>
              </>
            )}
          </td>
        );
      }
    };

    if (data.DiscussionStatus) {
      data.DiscussionStatus = {
        title: t('Disc.'),
        default: true,
        columnMenu: true,
        visible: true,
        xls: false,
        width: '45px',
        cell: ({ dataItem }) => {
          return (
            <td>
              <Link to={this.getDiscussionPath(dataItem.ServiceRequestId)}>
                <Discussion
                  serviceRequestId={dataItem.ServiceRequestId}
                  status={dataItem.DiscussionStatus}
                  iconOnly={true}
                  onDiscussionClose={this.queryOData}
                  initials={t('HN')}
                  isSeen={dataItem.IsLastDiscussionSeen}
                />
              </Link>
            </td>
          );
        },
        customFilter: {
          data: [
            {
              name: t('Select all'),
              default: true,
              value: ''
            },
            {
              name: t('Unread messages'),
              value: 1
            },
            {
              name: t('Read messages'),
              value: 2
            },
            {
              name: t('No messages'),
              value: 3
            }
          ]
        }
      };

      if (data.PendingFlag) {
        delete data.PendingFlag;
      }

      if (data.PendingDate) {
        delete data.PendingDate;
      }

      if (data.PendingComment) {
        delete data.PendingComment;
      }

      if (data.IsSharedCost) {
        delete data.IsSharedCost;
      }

      if (data.IsExcludedFromFsorConfirmation) {
        delete data.IsExcludedFromFsorConfirmation;
      }

      if (data.IsEarlyConfirmationRequired) {
        delete data.IsEarlyConfirmationRequired;
      }
    }

    return data;
  }

  getAssignButtonName() {
    const { t } = this.props;
    return t('Assign / Appoint');
  }

  getDisplayData() {
    if (this.props.oData.data.length > 0) {
      const displayData = [].concat(this.props.oData.data);
      displayData.map(item => {
        const rowItem = this.state.selectedRows[item.ServiceRequestId];
        item.selectedField = rowItem
          ? !this.state.isAllSelected
          : this.state.isAllSelected;
      });
      displayData.push({
        footer: true,
        FooterTotalActualCost: displayData[0].FooterTotalActualCost
          ? this.roundNumber(displayData[0].FooterTotalActualCost)
          : displayData[0].FooterTotalActualCost,
        FooterTotalCost: displayData[0].FooterTotalCost
          ? this.roundNumber(displayData[0].FooterTotalCost)
          : displayData[0].FooterTotalCost,
        FooterTotalGrossAmount: displayData[0].FooterTotalGrossAmount
          ? this.roundNumber(displayData[0].FooterTotalGrossAmount)
          : displayData[0].FooterTotalGrossAmount,
        FooterTotalActualGrossAmount: displayData[0]
          .FooterTotalActualGrossAmount
          ? this.roundNumber(displayData[0].FooterTotalActualGrossAmount)
          : displayData[0].FooterTotalActualGrossAmount
      });
      return displayData;
    }
    return [];
  }

  getXlsTotals = () => {
    const { XLSoData } = this.state;
    return {
      FooterTotalActualCost: XLSoData[0].FooterTotalActualCost
        ? this.roundNumber(XLSoData[0].FooterTotalActualCost)
        : XLSoData[0].FooterTotalActualCost,
      FooterTotalCost: XLSoData[0].FooterTotalCost
        ? this.roundNumber(XLSoData[0].FooterTotalCost)
        : XLSoData[0].FooterTotalCost,
      FooterTotalGrossAmount: XLSoData[0].FooterTotalGrossAmount
        ? this.roundNumber(XLSoData[0].FooterTotalGrossAmount)
        : XLSoData[0].FooterTotalGrossAmount,
      FooterTotalActualGrossAmount: XLSoData[0].FooterTotalActualGrossAmount
        ? this.roundNumber(XLSoData[0].FooterTotalActualGrossAmount)
        : XLSoData[0].FooterTotalActualGrossAmount
    };
  };

  getExcelAdditionalColumns(getGridColumns) {
    const { t } = this.props;
    const result = [];

    if (
      getGridColumns.ActualGrossAmount &&
      getGridColumns.ActualGrossAmount.visible
    ) {
      result.push(
        <ExcelExportColumn
          key={ShortId.generate()}
          field="ActualGrossAmount"
          title={t('Actual gross amount')}
          footer={() => {
            return (
              t('Sum: ') + this.getXlsTotals().FooterTotalActualGrossAmount
            );
          }}
        />
      );
    }

    if (getGridColumns.ActualCost && getGridColumns.ActualCost.visible) {
      result.push(
        <ExcelExportColumn
          key={ShortId.generate()}
          field="ActualCost"
          title={t('Actual cost')}
          footer={() => {
            return t('Sum: ') + this.getXlsTotals().FooterTotalActualCost;
          }}
        />
      );
    }

    if (getGridColumns.TotalCost && getGridColumns.TotalCost.visible) {
      result.push(
        <ExcelExportColumn
          key={ShortId.generate()}
          field="TotalCost"
          title={t('Total cost')}
          footer={() => {
            return t('Sum: ') + this.getXlsTotals().FooterTotalCost;
          }}
        />
      );
    }

    if (
      getGridColumns.TotalGrossCost &&
      getGridColumns.TotalGrossCost.visible
    ) {
      result.push(
        <ExcelExportColumn
          key={ShortId.generate()}
          field="TotalGrossCost"
          title={t('Total gross amount')}
          footer={() => {
            return t('Sum: ') + this.getXlsTotals().FooterTotalGrossAmount;
          }}
        />
      );
    }

    return result;
  }

  renderInvoiceCreateDialog() {
    return (
      <InvoiceCreateDialog
        reload={() => {
          this.queryOData();
          this.resetAfterSubmission();
        }}
        withIsPaidSwitch={false}
        serviceRequestIds={this.state.invoiceServiceRequestIds}
        isDialogOpen={this.state.openInvoiceDialog}
        closeDialog={() => {
          this.setState({
            openInvoiceDialog: false,
            invoiceServiceRequestIds: null
          });
        }}
      />
    );
  }

  assignDialog() {
    const { t, bulkAssignmentLoading } = this.props;
    return (
      <Dialog
        title={t('Bulk Action')}
        isOpen={this.state.openDialog}
        onClose={this.closeDialog}
      >
        <Tabs className="user-tabs">
          <Tabs.Tab
            component={
              <>
                <div className="annex-overview--dialog">
                  <Formik
                    validationSchema={this.validationSchema}
                    onSubmit={this.handleSubmit}
                    enableReinitialize={true}
                    initialValues={{ subcontractorId: null }}
                  >
                    {({ setFieldValue }) => (
                      <Form>
                        <Field
                          name="subcontractorId"
                          component={Dropdown}
                          label={t('Subcontractor')}
                        >
                          {this.toSubcontractorsList()}
                        </Field>
                        <Field
                          name="comment"
                          component={Textarea}
                          placeholder="Enter comment"
                          label={t('Comment')}
                        />
                        <Field
                          name="shouldSendEmail"
                          render={({ field }) => {
                            return (
                              <div className="switch-wrapper">
                                <Switch
                                  alignIndicator="right"
                                  name="shouldSendEmail"
                                  setFieldValue={(name, value) => {
                                    setFieldValue('shouldSendEmail', value);
                                  }}
                                  {...field}
                                >
                                  {`${t('Send Email')}:`}
                                </Switch>
                                <span className="send-value">
                                  {field.value ? t('Yes') : t('No')}
                                </span>
                              </div>
                            );
                          }}
                        />
                        <RaisedButton
                          type="submit"
                          disabled={bulkAssignmentLoading}
                        >
                          {t('Assign')}
                        </RaisedButton>
                      </Form>
                    )}
                  </Formik>
                </div>
              </>
            }
          >
            {t('Assign')}
          </Tabs.Tab>
          <Tabs.Tab
            component={
              <>
                <div className="annex-overview--dialog">
                  <Formik
                    validationSchema={this.validationSchemaAppoint}
                    onSubmit={this.handleAppointSubmit}
                    enableReinitialize={true}
                    initialValues={{ unitId: null }}
                  >
                    {({ isSubmitting }) => (
                      <Form>
                        <Field
                          name="unitId"
                          component={Dropdown}
                          label={t('Sub-unit')}
                        >
                          {this.getUnits('Select sub-unit')}
                        </Field>
                        <RaisedButton type="submit" disabled={isSubmitting}>
                          {t('Appoint')}
                        </RaisedButton>
                      </Form>
                    )}
                  </Formik>
                </div>
              </>
            }
          >
            {t('Appoint')}
          </Tabs.Tab>
        </Tabs>
      </Dialog>
    );
  }

  UnitsFilter() {
    return (
      <div className="service-requests-view--heading-filters">
        <ServiceRequestNavContext.Consumer>
          {nav => (
            <Dropdown
              large
              value={this.state.selectedUnit}
              onChange={event => {
                nav.clearSelectedUnit();
                this.handleUnitFilterChange(event.target.value);
              }}
            >
              {this.getUnits('All sub-units')}
            </Dropdown>
          )}
        </ServiceRequestNavContext.Consumer>
      </div>
    );
  }

  getUnits(text) {
    return this.getUnitOptions(text, this.flattenUnitsTree(this.props.units));
  }

  getUnitOptions = (text, units) => {
    const { t } = this.props;
    if (units && units.length) {
      return []
        .concat(
          [
            {
              unitName: t(text),
              value: ''
            }
          ],
          units
        )
        .map(unit => {
          return (
            <option key={ShortId.generate()} value={unit.unitId}>
              {unit.unitName}
            </option>
          );
        });
    }
    return <option value="">{t('There are no sub-units')}</option>;
  };

  handleAppointSubmit = async values => {
    const { t, bulkAppoint } = this.props;

    const serviceRequestIds = await this.getServiceRequestIdsForBulkAssign();

    const body = {
      serviceRequestIds,
      unitId: values.unitId ? values.unitId : null
    };

    bulkAppoint(body).then(response => {
      if (response && response.success === true) {
        Toast.success(t('Service requests appointed successfully'));
        this.queryOData();
        this.resetAfterSubmission();
      } else {
        Toast.warning(response.payload.message);
      }
    });
  };

  getGridFooter(getVisibleColumnCount, getGridColumns, dataItem) {
    const { t } = this.props;
    return (
      <tr className="k-master-row">
        <td colSpan={getVisibleColumnCount() + 1}>
          <div className="annex-overview--footer">
            <div className="annex-overview--footer--title">{t('Sum')}:</div>
            <div className="annex-overview--footer--items">
              {getGridColumns.ActualCost.visible && (
                <div className="annex-overview--footer--item">
                  <Tooltip tooltip={t('Actual cost')} position="top">
                    <>
                      <Money amount={dataItem.FooterTotalActualCost} />
                    </>
                  </Tooltip>
                </div>
              )}
              {getGridColumns.ActualGrossAmount &&
                getGridColumns.ActualGrossAmount.visible && (
                  <div className="annex-overview--footer--item">
                    <Tooltip
                      tooltip={t('Total Actual gross amount')}
                      position="top"
                    >
                      <>
                        <Money amount={dataItem.FooterTotalActualGrossAmount} />
                      </>
                    </Tooltip>
                  </div>
                )}
              <div className="annex-overview--footer--item">
                <Tooltip tooltip={t('Total cost')} position="top">
                  <>
                    <Money amount={dataItem.FooterTotalCost} />
                  </>
                </Tooltip>
              </div>
              {getGridColumns.TotalGrossCost.visible && (
                <div className="annex-overview--footer--item">
                  <Tooltip tooltip={t('Total gross amount')} position="top">
                    <>
                      <Money amount={dataItem.FooterTotalGrossAmount} />
                    </>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        </td>
      </tr>
    );
  }

  renderInvoiceDialog(preview, reload, isDialogOpen, closeDialog) {
    return (
      <InvoiceDialog
        preview={preview}
        reload={reload}
        isDialogOpen={isDialogOpen}
        closeDialog={closeDialog}
        withIsPaidSwitch={false}
      />
    );
  }
}

const oDataLoading = createLoadingSelector(['OData']);
const bulkAssignmentLoading = createLoadingSelector([
  'AssignmentServiceRequest/BulkAssignment'
]);
const mapStateToProps = state => ({
  currentExercise: state.exercise.current,
  isLoading: oDataLoading(state),
  oData: state.odata,
  annexOverviewGrid: state.gridSelections.annexOverviewGrid,
  subcontractors: state.assignmentServiceRequest.bulkForSubmit.subcontractors,
  units: state.assignmentServiceRequest.bulkForSubmit.units,
  bulkAssignmentLoading: bulkAssignmentLoading(state)
});

const mapDispatchToProps = {
  queryOData,
  queryXLSOData,
  resetOData,
  getAnnexes,
  getAnnexOverviewGrid,
  bulkAssignForSubmit,
  bulkAssign,
  bulkAppoint,
  canBulkAssign,
  getAnnexFilteredServiceRequestIds
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  withTranslation()(
    withRouter(
      WithVisibleColumns(
        WithAnnexFilter(WithQuickSearch(WithRoles(AnnexOverview)))
      )
    )
  )
);
